/**
 * "Friends are looking for" on the Profile's event cards: on or off.
 *
 * Owner direction 2026-09-04: a trainer with no friends should not see the
 * friends box on an event at all, and everyone gets a switch in Settings to
 * hide it. The switch is this preference (default ON, device-local like the
 * other display prefs); the no-friends rule is decided where the box renders
 * (shouldShowFriendTrades) from the friends list the box already fetches.
 *
 * Same shape as eventDexGapsPref: a cached localStorage boolean plus a window
 * event so every mounted card flips together.
 */
const STORAGE_KEY = "nexus.friendTrades.v1";

export const FRIEND_TRADES_EVENT = "nexus:friend-trades-changed";

let cache: boolean | null = null;

function loadFromStorage(): boolean {
  if (cache !== null) return cache;
  if (typeof window === "undefined") {
    cache = true;
    return cache;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    // Default ON: a missing key means the trainer has never set the pref.
    cache = raw === null ? true : raw === "true";
    return cache;
  } catch {
    cache = true;
    return cache;
  }
}

function emit(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(FRIEND_TRADES_EVENT));
}

/** Whether the trainer wants the friends box on event cards. */
export function isFriendTradesEnabled(): boolean {
  return loadFromStorage();
}

export function setFriendTradesEnabled(enabled: boolean): void {
  cache = enabled;
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, enabled ? "true" : "false");
    } catch {
      // ignore quota errors: the in-memory cache keeps this session consistent
    }
  }
  emit();
}

/**
 * The one rule for the box: the pref must be on, and the trainer must have
 * at least one friend once the list has loaded. While the list is still
 * loading the box stays hidden too, so it never flashes "add friends" at a
 * trainer who has them.
 */
export function shouldShowFriendTrades(input: {
  enabled: boolean;
  friendCount: number;
  friendsLoaded: boolean;
}): boolean {
  if (!input.enabled) return false;
  if (!input.friendsLoaded) return false;
  return input.friendCount > 0;
}

/** Test-only helper: reset the in-memory cache. */
export function __resetFriendTradesCacheForTests(): void {
  cache = null;
}
